import { downloadTextFile, reportDateRangeFilenamePart, rowsToCsv } from '@/api/csvExport';
import { reportAdProductShortLabel } from '@/api/reportAdProducts';
import {
  campaignReportColumnLabel,
  keywordReportColumnLabel,
  targetReportColumnLabel,
  type CampaignReportColumnId,
  type KeywordReportColumnId,
  type TargetReportColumnId,
  visibleCampaignReportColumns,
  visibleKeywordReportColumns,
  visibleTargetReportColumns,
} from '@/api/reportTableColumns';

export type ReportExportRow = {
  adProduct?: string;
  campaignName?: string;
  campaignId?: string;
  keywordText?: string;
  matchType?: string;
  targetId?: string;
  targetingText?: string;
  adGroupId?: string;
  impressions?: number;
  clicks?: number;
  cost?: number;
  purchases30d?: number;
  sales30d?: number;
  ctr?: number;
  roas?: number;
};

function parseExportNumber(value: unknown): number | '' {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : '';
  }
  return '';
}

function campaignReportExportValue(
  columnId: CampaignReportColumnId,
  row: ReportExportRow
): string | number {
  switch (columnId) {
    case 'type':
      return reportAdProductShortLabel(row.adProduct || 'sponsored_products');
    case 'campaign':
      return row.campaignName || '';
    case 'impressions':
      return parseExportNumber(row.impressions);
    case 'clicks':
      return parseExportNumber(row.clicks);
    case 'spend':
      return parseExportNumber(row.cost);
    case 'purchases':
      return parseExportNumber(row.purchases30d);
    case 'sales':
      return parseExportNumber(row.sales30d);
    case 'ctr':
      return parseExportNumber(row.ctr);
    case 'roas':
      return parseExportNumber(row.roas);
    default:
      return '';
  }
}

function keywordReportExportValue(
  columnId: KeywordReportColumnId,
  row: ReportExportRow
): string | number {
  switch (columnId) {
    case 'type':
      return reportAdProductShortLabel(row.adProduct || 'sponsored_products');
    case 'keyword':
      return row.keywordText || '';
    case 'match':
      return row.matchType || '';
    case 'impressions':
      return parseExportNumber(row.impressions);
    case 'clicks':
      return parseExportNumber(row.clicks);
    case 'spend':
      return parseExportNumber(row.cost);
    case 'purchases':
      return parseExportNumber(row.purchases30d);
    case 'sales':
      return parseExportNumber(row.sales30d);
    case 'ctr':
      return parseExportNumber(row.ctr);
    case 'roas':
      return parseExportNumber(row.roas);
    default:
      return '';
  }
}

export function exportCampaignReportCsv(
  rows: ReportExportRow[],
  options: {
    startDate: string;
    endDate: string;
    selectedColumns: CampaignReportColumnId[];
    showTypeColumn: boolean;
  }
): void {
  const visibleColumns = visibleCampaignReportColumns(options.selectedColumns, options.showTypeColumn);
  const headers = visibleColumns.map((columnId) => campaignReportColumnLabel(columnId));
  const csvRows = rows.map((row) =>
    visibleColumns.map((columnId) => campaignReportExportValue(columnId, row))
  );
  const csv = rowsToCsv(headers, csvRows);
  const range = reportDateRangeFilenamePart(options.startDate, options.endDate);
  downloadTextFile(`campaign-report_${range}.csv`, csv);
}

export function exportKeywordReportCsv(
  rows: ReportExportRow[],
  options: {
    startDate: string;
    endDate: string;
    selectedColumns: KeywordReportColumnId[];
    showTypeColumn: boolean;
  }
): void {
  const visibleColumns = visibleKeywordReportColumns(options.selectedColumns, options.showTypeColumn);
  const headers = visibleColumns.map((columnId) => keywordReportColumnLabel(columnId));
  const csvRows = rows.map((row) =>
    visibleColumns.map((columnId) => keywordReportExportValue(columnId, row))
  );
  const csv = rowsToCsv(headers, csvRows);
  const range = reportDateRangeFilenamePart(options.startDate, options.endDate);
  downloadTextFile(`keyword-report_${range}.csv`, csv);
}

function targetReportExportValue(columnId: TargetReportColumnId, row: ReportExportRow): string | number {
  switch (columnId) {
    case 'target':
      return row.targetId || '';
    case 'targeting':
      return row.targetingText || '';
    case 'campaign':
      return row.campaignName || row.campaignId || '';
    case 'adGroup':
      return row.adGroupId || '';
    case 'impressions':
      return parseExportNumber(row.impressions);
    case 'clicks':
      return parseExportNumber(row.clicks);
    case 'spend':
      return parseExportNumber(row.cost);
    case 'purchases':
      return parseExportNumber(row.purchases30d);
    case 'sales':
      return parseExportNumber(row.sales30d);
    case 'ctr':
      return parseExportNumber(row.ctr);
    case 'roas':
      return parseExportNumber(row.roas);
    default:
      return '';
  }
}

export function exportTargetReportCsv(
  rows: ReportExportRow[],
  options: {
    startDate: string;
    endDate: string;
    selectedColumns: TargetReportColumnId[];
  }
): void {
  const visibleColumns = visibleTargetReportColumns(options.selectedColumns);
  const headers = visibleColumns.map((columnId) => targetReportColumnLabel(columnId));
  const csvRows = rows.map((row) =>
    visibleColumns.map((columnId) => targetReportExportValue(columnId, row))
  );
  const csv = rowsToCsv(headers, csvRows);
  const range = reportDateRangeFilenamePart(options.startDate, options.endDate);
  downloadTextFile(`target-report_${range}.csv`, csv);
}
